import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Play, Star, Quote } from "lucide-react";
import { getProducts, getProjects, getTestimonials, getClients, getSettings } from "../api";

function Counter({ end, suffix = "", duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef();
  useEffect(() => {
    let started = false;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          const start = Date.now();
          const tick = () => {
            const t = Math.min(1, (Date.now() - start) / duration);
            setVal(Math.floor(end * (0.5 - Math.cos(Math.PI * t) / 2)));
            if (t < 1) requestAnimationFrame(tick);
            else setVal(end);
          };
          tick();
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

const parseStatNum = (s) => { const m = String(s || "").match(/(\d+)/); return m ? parseInt(m[1], 10) : 0; };
const parseStatSuffix = (s) => String(s || "").replace(/[0-9]/g, "");

const Home = () => {
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    getProducts().then(setProducts);
    getProjects().then(setProjects);
    getTestimonials().then(setTestimonials);
    getClients().then(setClients);
    getSettings().then(setSettings);
  }, []);

  const stats = [
    { n: parseStatNum(settings.stat_cubic_meters), s: parseStatSuffix(settings.stat_cubic_meters) || "K+", l: "m³ delivered" },
    { n: parseStatNum(settings.stat_years), s: parseStatSuffix(settings.stat_years) || "+", l: "Years experience" },
    { n: parseStatNum(settings.stat_plants), s: parseStatSuffix(settings.stat_plants) || "", l: "Modern plants" },
    { n: parseStatNum(settings.stat_projects), s: parseStatSuffix(settings.stat_projects) || "+", l: "Landmark projects" },
  ];

  const t = (key, fallback) => settings[key] || fallback;

  return (
    <main>
      {/* HERO */}
     {/* <section className="relative min-h-[92vh] overflow-hidden bg-[#fafafa]">
        <video autoPlay muted loop playsInline poster={settings.hero_poster || "https://images.unsplash.com/photo-1599995903128-531fc7fb694b?w=1920&q=80"} className="absolute inset-0 w-full h-full object-cover">
          <source src={settings.hero_video || "https://cdn.pixabay.com/video/2020/09/08/49375-457952419_large.mp4"} type="video/mp4"/>
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/75 to-[#fafafa]"/>
        <div className="absolute inset-0 grid-bg opacity-60"/>
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-20 blur-3xl" style={{background:'radial-gradient(ellipse, #ef2435, transparent 70%)'}}/>

        <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-4 z-10">
          <div className="w-px h-24 bg-gradient-to-b from-transparent to-[#ef2435]"/>
          <p className="vertical-text text-[10px] tracking-[0.3em] text-gray-500 font-mono">EST. 2010 · SSC READY MIX</p>
          <div className="w-px h-24 bg-gradient-to-t from-transparent to-[#ef2435]"/>
        </div>

        <div className="relative max-w-[1500px] mx-auto px-5 md:px-8 pt-24 pb-20 min-h-[92vh] flex flex-col justify-between">
          <div className="grid lg:grid-cols-[1.5fr,1fr] gap-12 items-end pt-16 lg:pt-24">
            <div>
              <div className="red-tag mb-8">{t("home_hero_tag", "Premium Ready-Mix Concrete")}</div>
              <h1 className="giant-text text-[clamp(1rem,11vw,3rem)] font-bold mb-2 " style={{lineHeight:1.2}}>
                {t("home_hero_title_1", "")}<br/>{t("home_hero_title_2", "")} <span className="giant-red">{t("home_hero_title_3", ".")}</span>
              </h1>
              <p className="text-gray-600 text-lg max-w-xl mt-8 leading-relaxed">{t("home_hero_description", "Engineered concrete solutions for builders, contractors and government projects. Strength, precision and on-time deliveryevery batch, every time.")}</p>
              <div className="flex flex-wrap gap-3 mt-10">
                <Link to="/products" className="btn-primary px-7 py-4 rounded-full text-sm font-semibold inline-flex items-center gap-2">{t("home_hero_cta1", "Explore Products")} <ArrowUpRight size={18}/></Link>
                <Link to="/diy" className="btn-ghost px-7 py-4 rounded-full text-sm font-semibold inline-flex items-center gap-2 bg-white"><Play size={14}/> {t("home_hero_cta2", "Calculate concrete")}</Link>
              </div>
            </div>

            <div className="card-dark rounded-2xl p-6 lg:p-8 backdrop-blur">
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[11px] text-[#ef2435] tracking-widest font-semibold">[ LIVE_STATS ]</span>
                <span className="flex items-center gap-2 text-[11px] text-gray-500 font-mono"><span className="w-1.5 h-1.5 rounded-full bg-[#ef2435] pulse-red"/>ACTIVE</span>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {stats.map((s, i) => (
                  <div key={i}>
                    <p className="font-display text-5xl text-gray-900"><Counter end={s.n} suffix={s.s}/></p>
                    <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{s.l}</p>
                  </div>
                ))}
              </div>
              <div className="stat-line my-6"/>
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-gray-500">UPTIME</span>
                <span className="text-[#ef2435] font-semibold">99.7%</span>
              </div>
            </div>
          </div>

          <div className="flex items-end justify-between mt-12">
            <div className="flex items-center gap-3 text-xs font-mono text-gray-500">
              <span className="w-8 h-px bg-[#ef2435]"/>
              <span>SCROLL TO EXPLORE</span>
            </div>
            <div className="text-xs font-mono text-gray-500 hidden md:block">N · 20.2961° · E 85.8245°</div>
          </div>
        </div>
      </section>*/}

      {/* HERO */}
<section className="relative min-h-screen overflow-hidden bg-black">
  {/* Background Image Slider */}
  <div className="absolute inset-0">
    {[
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1920",
      "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=1920",
      "https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=1920",
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1920",
    ].map((img, i) => (
      <div
        key={i}
        className="absolute inset-0 animate-hero-slider"
        style={{
          backgroundImage: `url(${img})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          animationDelay: `${i * 5}s`,
        }}
      />
    ))}

    <div className="absolute inset-0 bg-black/60" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/60" />
  </div>

  {/* Content */}
  <div className="relative z-10 max-w-[1500px] mx-auto px-5 md:px-8 min-h-screen flex items-center">
    <div className="grid lg:grid-cols-2 gap-16 items-center w-full">

      {/* LEFT CONTENT */}
      <div>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 backdrop-blur text-white text-xs tracking-[0.2em] uppercase mb-8">
          Premium Ready Mix Concrete
        </div>

        <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-[0.9] tracking-tight">
          SSC
          <br />
          <span className="text-[#ef2435]">READYMIX</span>
        </h1>

        <h2 className="mt-6 text-2xl md:text-4xl font-light text-gray-200 tracking-wide">
          WE CONCRETE YOUR DREAM
        </h2>

        {/* Punch Lines */}
        <div className="mt-10 space-y-4">
          {[
            "Beyond Concrete. Built on Absolute Trust.",
            "Engineered Precision. Proven Performance.",
            "Defined by Quality. Delivered with Discipline.",
            "Trusted Over Time.",
          ].map((line, i) => (
            <div
              key={i}
              className="flex items-start gap-3 text-white/90"
            >
              <div className="w-2 h-2 rounded-full bg-[#ef2435] mt-2" />
              <p className="text-lg md:text-xl">{line}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-wrap gap-4 mt-12">
          <Link
            to="/contact"
            className="bg-[#ef2435] hover:bg-[#d61d2e] transition-all text-white px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 shadow-2xl"
          >
            Request a Quote
            <ArrowUpRight size={18} />
          </Link>

          <Link
            to="/contact"
            className="bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur text-white px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2"
          >
            Contact Us
          </Link>
        </div>
      </div>

      {/* RIGHT VISUAL CARDS */}
      <div className="hidden lg:grid grid-cols-2 gap-5">
        {[
          {
            title: "",
            img: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=800",
          },
          {
            title: "",
            img: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=800",
          },
          {
            title: "",
            img: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=800",
          },
          {
            title: "",
            img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=800",
          },
        ].map((item, i) => (
          <div
            key={i}
            className="relative rounded-3xl overflow-hidden h-64 group border border-white/10"
          >
            <img
              src={item.img}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

            <div className="absolute bottom-5 left-5">
              <p className="text-white text-2xl font-bold">
                {item.title}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>

  {/* Bottom Scroll */}
  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em]">
    SCROLL TO EXPLORE
  </div>
</section>

      {/* Marquee band */}
      <section className="py-8 bg-[#ef2435] overflow-hidden">
        <div className="marquee-fast">
          {[...Array(8)].map((_,i)=>(
            <div key={i} className="flex items-center gap-8 px-6 shrink-0">
              <span className="font-display text-3xl md:text-5xl text-white">QUALITY</span>
              <span className="text-white text-2xl">✕</span>
              <span className="font-display text-3xl md:text-5xl text-white">STRENGTH</span>
              <span className="text-white text-2xl">✕</span>
              <span className="font-display text-3xl md:text-5xl text-white">PRECISION</span>
              <span className="text-white text-2xl">✕</span>
              <span className="font-display text-3xl md:text-5xl text-white">TRUST</span>
              <span className="text-white text-2xl">✕</span>
            </div>
          ))}
        </div>
      </section>

      
    {/* ABOUT PREVIEW */}
<section className="py-24 bg-white relative overflow-hidden">
  <div className="absolute inset-0 grid-bg opacity-30" />

  <div className="max-w-[1500px] mx-auto px-5 md:px-8 relative">

    {/* Heading */}
   <div className="grid xl:grid-cols-[45%,55%] gap-14 xl:gap-20 items-center mb-28">

  {/* LEFT SIDE */}

  <div>

    <div className="inline-flex items-center gap-2 rounded-full border border-[#ececef] bg-[#fafafa] px-5 py-2 mb-8">
      <span className="w-2 h-2 rounded-full bg-[#ef2435]" />

      <span className="text-[11px] tracking-[0.3em] uppercase font-semibold text-[#ef2435]">
        About SSC Readymix
      </span>
    </div>

   <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-6">
  About SSC READYMIX
</h2>

    <div className="w-24 h-[4px] bg-[#ef2435] rounded-full mb-8" />

    <p className="text-gray-600 text-[17px] leading-[2] max-w-2xl">
      SSC READYMIX stands as a trusted provider of engineered
      ready mix concrete solutions, supporting the growing
      demands of modern construction through precision driven
      processes, consistent quality standards, and disciplined
      execution.
    </p>
  </div>

  {/* RIGHT SIDE */}

  <div className="relative">

    {/* BG GLOW */}

    <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-[#ef2435]/5 blur-3xl" />

    <div className="relative rounded-[36px] border border-[#ececef] bg-white p-8 md:p-10 shadow-[0_20px_70px_rgba(0,0,0,0.05)] overflow-hidden">

      {/* TOP */}

      <div className="flex items-center justify-between gap-5 mb-10">

        <div>
          <p className="text-[#ef2435] text-xs uppercase tracking-[0.3em] font-semibold mb-2">
            Why Choose
          </p>

          <h3 className="text-3xl md:text-4xl font-black text-gray-900 leading-none">
            SSC READYMIX
          </h3>
        </div>

        <div className="w-14 h-14 rounded-2xl bg-[#ef2435] flex items-center justify-center shadow-lg shadow-red-500/20">
          <ArrowUpRight className="text-white" size={22} />
        </div>
      </div>

      {/* FEATURES */}

      <div className="grid sm:grid-cols-2 gap-4">

        {[
          "Automated Batching Systems",
          "GPS Enabled Transit Mixers",
          "Timely Delivery",
          "Strict Quality Control",
          "Customized Mix Design Solutions",
          "Experienced Technical Team",
        ].map((item, i) => (
          <div
            key={i}
            className="group flex items-start gap-4 rounded-2xl border border-[#ececef] bg-[#fafafa] px-5 py-5 hover:bg-white hover:border-[#ef2435] transition-all duration-300"
          >

            <div className="w-8 h-8 rounded-full bg-[#ef2435] flex items-center justify-center shrink-0 shadow-md shadow-red-500/20">
              <span className="text-white text-[11px] font-bold">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>

            <p className="text-gray-700 text-sm leading-7 font-semibold">
              {item}
            </p>
          </div>
        ))}
      </div>
    </div>
  </div>
</div>

    {/* PRODUCTS PREVIEW */}
    <div className="mb-28">
      <div className="flex items-end justify-between mb-10">
        <div>
          <span className="section-eyebrow">
            [ PRODUCTS PREVIEW ]
          </span>

          <h2 className="text-4xl md:text-6xl font-bold leading-tight">
            PREMIUM CONCRETE
            <br />
            SOLUTIONS.
          </h2>
        </div>

        <Link
          to="/products"
          className="btn-primary px-6 py-3 rounded-full text-sm font-semibold"
        >
          View All Products
        </Link>
      </div>

     <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">

  {(products?.length
    ? products.slice(0, 4)
    : [
        {
          name: "M20 Grade",
          type: "Residential",
          desc: "Reliable concrete for residential structures and slabs.",
        },
        {
          name: "M25 Grade",
          type: "Commercial",
          desc: "High-strength mix for commercial buildings.",
        },
        {
          name: "M30 Grade",
          type: "Infrastructure",
          desc: "Precision engineered for infrastructure projects.",
        },
        {
          name: "Self Compacting",
          type: "Premium",
          desc: "Advanced flowable concrete for complex structures.",
        },
      ]
  ).map((p, i) => (

    <div
      key={i}
      className="group relative overflow-hidden rounded-[30px] border border-[#ececef] bg-white hover:border-[#ef2435] transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl h-full flex flex-col"
    >

      {/* TOP RED GLOW */}
      <div className="absolute top-0 left-0 w-full h-1 bg-[#ef2435]" />

      {/* BACKGROUND EFFECT */}
      <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-[#ef2435]/5 group-hover:bg-[#ef2435]/10 transition-all duration-500" />

      {/* CARD CONTENT */}
      <div className="relative p-8 flex flex-col h-full">

        {/* HEADER */}
        <div className="flex items-start justify-between mb-8">

          <div>
            <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-[#ef2435]/10 text-[#ef2435] text-[10px] font-semibold tracking-[0.2em] uppercase mb-4">
              {p.type}
            </div>

            <h3 className="text-3xl font-bold text-gray-900 leading-tight">
              {p.name}
            </h3>
          </div>

          <div className="w-11 h-11 rounded-2xl border border-[#ececef] flex items-center justify-center group-hover:bg-[#ef2435] group-hover:border-[#ef2435] transition-all shrink-0">
            <ArrowUpRight
              size={18}
              className="text-gray-700 group-hover:text-white transition-all"
            />
          </div>

        </div>

        {/* DESCRIPTION */}
        <div className="flex-1">

          <p className="text-gray-600 leading-relaxed text-[15px] mb-8">
            {p.description || p.desc}
          </p>

          {/* FEATURES */}
          <div className="space-y-3 mb-8">

            {[
              "High Strength",
              "Consistent Quality",
              "Timely Delivery",
            ].map((f, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3"
              >
                <div className="w-2 h-2 rounded-full bg-[#ef2435]" />

                <span className="text-sm text-gray-700 font-medium">
                  {f}
                </span>
              </div>
            ))}

          </div>

        </div>

        {/* FOOTER */}
        <div className="pt-6 border-t border-[#ececef] mt-auto">

          {/*<button className="w-full bg-[#111] hover:bg-[#ef2435] text-white py-4 rounded-2xl text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2">
            Inquiry Now
            <ArrowRight size={16} />
          </button>*/}

          <Link
  to={`/products?product=${i}`}
  className="w-full bg-[#111] hover:bg-[#ef2435] text-white py-4 rounded-2xl text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2"
>
  View Product
  <ArrowRight size={16} />
</Link>

        </div>

      </div>

    </div>

  ))}

</div>
    </div>

    {/* QUALITY + DELIVERY */}
   {/* QUALITY + OPERATIONS SECTION */}
<section className="mb-32">
  {/* SECTION HEADER */}
  <div className="text-center mb-16">
    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#ececef] bg-[#fafafa] mb-6">
      <span className="w-2 h-2 rounded-full bg-[#ef2435]" />
      <span className="text-[11px] tracking-[0.3em] uppercase font-semibold text-[#ef2435]">
        Operational Excellence
      </span>
    </div>

    <h2 className="text-4xl md:text-6xl font-black text-gray-900 leading-tight">
      Precision.
      <span className="text-[#ef2435]"> Performance.</span>
      <br />
      Reliability.
    </h2>

    <p className="max-w-3xl mx-auto text-gray-600 text-lg leading-8 mt-6">
      SSC READYMIX combines engineered precision, disciplined execution,
      timely logistics, and transparent coordination to ensure seamless
      project delivery.
    </p>
  </div>

  {/* GRID */}
  <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">

    {/* CARD 1 */}
    <div className="group relative overflow-hidden rounded-[30px] bg-[#111] text-white p-8 hover:-translate-y-2 transition-all duration-500">

      <div className="absolute top-0 left-0 w-full h-1 bg-[#ef2435]" />
      <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-[#ef2435]/10 blur-3xl group-hover:bg-[#ef2435]/20 transition-all duration-500" />

      <div className="relative z-10">
        <div className="w-14 h-14 rounded-2xl bg-[#ef2435]/20 border border-[#ef2435]/20 flex items-center justify-center mb-6">
          <span className="text-2xl">⚙️</span>
        </div>

        <p className="text-[#ef2435] text-xs tracking-[0.3em] uppercase font-semibold mb-4">
          QUALITY & SAFETY
        </p>

        <h3 className="text-2xl font-black leading-tight mb-5">
          Engineered Precision.
          <br />
          Assured Performance.
        </h3>

        <p className="text-gray-300 leading-7 text-[15px]">
          Integrated quality control across batching, production,
          transit, and placement ensures strength, durability,
          consistency, and operational reliability.
        </p>
      </div>
    </div>

    {/* CARD 2 */}
    <div className="group relative overflow-hidden rounded-[30px] bg-[#ef2435] text-white p-8 hover:-translate-y-2 transition-all duration-500">

      <div className="absolute -bottom-20 -left-20 w-52 h-52 rounded-full bg-white/10 blur-3xl" />

      <div className="relative z-10">
        <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mb-6">
          <span className="text-2xl">⏱️</span>
        </div>

        <p className="text-white/80 text-xs tracking-[0.3em] uppercase font-semibold mb-4">
          EFFICIENT TIME MANAGEMENT
        </p>

        <h3 className="text-2xl font-black leading-tight mb-5">
          Planned Dispatch.
          <br />
          Timely Delivery.
        </h3>

        <p className="text-white/90 leading-7 text-[15px] mb-5">
          Structured dispatch planning, GPS fleet tracking,
          and coordinated site scheduling ensure smooth
          execution without delays.
        </p>

        <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-semibold">
          ZERO DELAYS, ONLY DELIVERY
        </div>
      </div>
    </div>

    {/* CARD 3 */}
    <div className="group relative overflow-hidden rounded-[30px] border border-[#ececef] bg-white p-8 hover:border-[#ef2435] hover:-translate-y-2 hover:shadow-2xl transition-all duration-500">

      <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-[#ef2435]/5 blur-3xl group-hover:bg-[#ef2435]/10 transition-all duration-500" />

      <div className="relative z-10">
        <div className="w-14 h-14 rounded-2xl bg-[#ef2435]/10 flex items-center justify-center mb-6">
          <span className="text-2xl">📡</span>
        </div>

        <p className="text-[#ef2435] text-xs tracking-[0.3em] uppercase font-semibold mb-4">
          TRANSPARENT COMMUNICATION
        </p>

        <h3 className="text-2xl font-black text-gray-900 leading-tight mb-5">
          Clear Communication.
          <br />
          Complete Visibility.
        </h3>

        <p className="text-gray-600 leading-7 text-[15px] mb-5">
          Real time updates, structured coordination,
          and transparent operational communication
          ensure complete project visibility.
        </p>

        <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#fafafa] border border-[#ececef] text-sm font-semibold text-gray-700">
          Total Confidence
        </div>
      </div>
    </div>

    {/* CARD 4 */}
    <div className="group relative overflow-hidden rounded-[30px] border border-[#ececef] bg-[#fafafa] p-8 hover:bg-white hover:border-[#ef2435] hover:-translate-y-2 hover:shadow-2xl transition-all duration-500">

      <div className="absolute -bottom-16 -right-16 w-44 h-44 rounded-full bg-[#ef2435]/5 blur-3xl group-hover:bg-[#ef2435]/10 transition-all duration-500" />

      <div className="relative z-10">
        <div className="w-14 h-14 rounded-2xl bg-[#ef2435] text-white flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
          <span className="text-2xl">🚚</span>
        </div>

        <p className="text-[#ef2435] text-xs tracking-[0.3em] uppercase font-semibold mb-4">
          DELIVERY OPERATIONS
        </p>

        <h3 className="text-2xl font-black text-gray-900 leading-tight mb-5">
          GPS Tracking.
          <br />
          Controlled Transit.
        </h3>

        <p className="text-gray-600 leading-7 text-[15px]">
          GPS enabled transit mixers and dispatch monitoring
          maintain delivery timelines, slump retention,
          and on site concrete performance.
        </p>
      </div>
    </div>

  </div>
</section>

    {/* VISION SECTION */}
{/* VISION SECTION */}
<div className="mb-28">
  <div className="flex items-end justify-between mb-16 hidden">
    <div>
      <span className="section-eyebrow">
        [ LEADERSHIP VISION ]
      </span>

      <h2 className="text-5xl md:text-6xl font-bold leading-tight">
        BUILDING
        <span className="text-[#ef2435]"> TRUST.</span>
      </h2>
    </div>
  </div>

  <div className="space-y-12 hidden">

    {[
      {
        name: "Managing Director",
        img: "https://admin.sscreadymix.com/uploads/Benudhar.jpg?v=${new Date().getTime()",
        text: "Our vision is to redefine construction standards through precision-engineered concrete solutions, operational discipline, and uncompromising quality that builds long-term trust with every project.",
      },
      {
        name: "Chief Executive Officer",
        img: "https://admin.sscreadymix.com/uploads/Shanti.jpg",
        text: "We believe modern infrastructure deserves dependable partners. SSC READYMIX is committed to timely delivery, technical excellence, and customer-focused execution across every scale of construction.",
      },
    ].map((v, i) => (
      <div
        key={i}
        className={`grid md:grid-cols-2 gap-8 items-center ${
          i % 2 !== 0 ? "md:[&>*:first-child]:order-2" : ""
        }`}
      >

        {/* IMAGE */}
        <div className="relative">
          <div className="relative rounded-[28px] overflow-hidden h-[320px] md:h-[360px]">
            <img
              src={v.img}
              alt={v.name}
              className="w-full h-full object-cover"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>

          {/* Floating Tag */}
          <div className="absolute -bottom-5 left-6 bg-white rounded-2xl shadow-xl px-5 py-3 border border-[#ececef]">
            <p className="text-xs uppercase tracking-[0.25em] text-[#ef2435] font-semibold mb-1">
              Leadership
            </p>

            <h3 className="text-lg font-bold text-gray-900">
              {v.name}
            </h3>
          </div>
        </div>

        {/* TEXT */}
        <div className="md:px-6 pt-8 md:pt-0">
          <div className="inline-flex items-center gap-2 mb-5">
            <div className="w-10 h-[2px] bg-[#ef2435]" />

            <span className="text-xs tracking-[0.3em] uppercase text-[#ef2435] font-semibold">
              Vision Statement
            </span>
          </div>

          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-6">
            Leadership Driven.
            <br />
            Future Focused.
          </h3>

          <p className="text-gray-600 leading-relaxed text-lg">
            {v.text}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <div className="px-4 py-2 rounded-full bg-[#fafafa] border border-[#ececef] text-sm font-medium text-gray-700">
              Quality Focused
            </div>

            <div className="px-4 py-2 rounded-full bg-[#fafafa] border border-[#ececef] text-sm font-medium text-gray-700">
              Timely Execution
            </div>

            <div className="px-4 py-2 rounded-full bg-[#fafafa] border border-[#ececef] text-sm font-medium text-gray-700">
              Trusted Operations
            </div>
          </div>
        </div>

      </div>
    ))}

  </div>
</div>

    {/* GALLERY */}
    <div className="mb-28">
      <div className="mb-12">
        <span className="section-eyebrow">
          [ GALLERY PREVIEW ]
        </span>

        <h2 className="text-5xl md:text-6xl font-bold">
          OPERATIONS IN MOTION.
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1200",
          "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=1200",
          "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1200",
          "https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=1200",
        ].map((img, i) => (
          <div
            key={i}
            className={`overflow-hidden rounded-3xl ${
              i === 0 || i === 3 ? "md:col-span-2" : ""
            }`}
          >
            <img
              src={img}
              alt=""
              className="w-full h-[280px] object-cover hover:scale-110 transition duration-700"
            />
          </div>
        ))}
      </div>
    </div>

    {/* TESTIMONIALS */}
    <div className="mb-28">
      <div className="mb-12">
        <span className="section-eyebrow">
          [ TESTIMONIALS ]
        </span>

        <h2 className="text-5xl md:text-6xl font-bold">
          TRUSTED BY BUILDERS.
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {(testimonials?.length
          ? testimonials.slice(0, 3)
          : [
              {
                name: "Infrastructure Contractor",
                text: "SSC READYMIX consistently delivers quality concrete on time.",
              },
              {
                name: "Project Engineer",
                text: "Professional operations and reliable batching quality.",
              },
              {
                name: "Site Supervisor",
                text: "Excellent support team and smooth delivery coordination.",
              },
            ]
        ).map((t, i) => (
          <div
            key={i}
            className="rounded-3xl bg-[#fafafa] border border-[#ececef] p-8"
          >
            <Quote className="text-[#ef2435]/20 mb-6" size={40} />

            <p className="text-gray-700 leading-relaxed mb-8">
              "{t.content || t.text}"
            </p>

            <div>
              <p className="font-semibold text-gray-900">
                {t.name}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* FINAL CTA */}
    <div className="rounded-[40px] bg-[#111] text-white p-12 md:p-20 text-center relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20" />

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[#ef2435]/10 blur-3xl" />

      <div className="relative">
        <span className="section-eyebrow justify-center inline-flex text-white">
          [ LET'S BUILD TOGETHER ]
        </span>

        <h2 className="text-5xl md:text-7xl font-bold leading-[0.9] mt-6 mb-8">
          Clear Communication.
          <br />
          <span className="text-[#ef2435]">
            Complete Visibility.
          </span>
          <br />
          Total Confidence.
        </h2>

        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <Link
            to="/contact"
            className="bg-[#ef2435] hover:bg-[#d81f2f] px-8 py-4 rounded-full font-semibold"
          >
            Request a Quote
          </Link>

          <Link
            to="/contact"
            className="bg-white/10 border border-white/20 backdrop-blur px-8 py-4 rounded-full font-semibold"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>

  </div>
</section>



      {/* WHY SSC - Bento */}
      <section className="py-24 md:py-32 bg-[#fafafa] hidden">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 mb-16 items-end">
            <div>
              <span className="section-eyebrow">{t("home_why_eyebrow", "[ 01 / WHY_SSC ]")}</span>
              <h2 className="giant-text text-5xl md:text-7xl font-bold leading-[0.9]">{t("home_why_title", "FORGED IN EXCELLENCE.")}</h2>
            </div>
            <p className="text-gray-600 leading-relaxed text-lg">{t("home_why_description", "Every batch we produce is the result of decades of engineering, rigorous testing and an unwavering commitment to the people who build with us.")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 auto-rows-[180px]">
            <div className="md:col-span-3 md:row-span-2 card-dark rounded-3xl p-8 relative overflow-hidden group">
              <div className="absolute top-6 right-6 w-12 h-12 rounded-full bg-[#ef2435]/10 flex items-center justify-center"><span className="font-mono text-[#ef2435] text-sm font-semibold">01</span></div>
              <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-[#ef2435]/5 group-hover:bg-[#ef2435]/10 transition-colors"/>
              <div className="relative h-full flex flex-col justify-end">
                <h3 className="font-display text-3xl md:text-5xl text-gray-900 mb-3 whitespace-pre-line">{t("home_bento_1_title", "ELITE QUALITY")}</h3>
                <p className="text-gray-600 leading-relaxed max-w-md">{t("home_bento_1_desc", "Stringent QC at every stageraw material testing, precise mix designs, and lab-verified strength reports for every truck.")}</p>
              </div>
            </div>
            <div className="md:col-span-3 card-dark rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute top-6 right-6 font-mono text-[#ef2435] text-sm font-semibold">[ 02 ]</div>
              <h3 className="font-display text-2xl md:text-3xl text-gray-900 mb-3">{t("home_bento_2_title", "ON-TIME, EVERY TIME")}</h3>
              <p className="text-gray-600 leading-relaxed text-sm">{t("home_bento_2_desc", "Optimized routing & GPS-tracked fleet ensures concrete arrives within its workable window.")}</p>
            </div>
            <div className="md:col-span-2 card-dark rounded-3xl p-7 relative overflow-hidden">
              <div className="font-display text-5xl md:text-6xl text-[#ef2435] mb-2">{t("home_bento_3_stat", "99%")}</div>
              <p className="text-gray-600 text-sm">{t("home_bento_3_text", "Zero QC rejection across landmark projects")}</p>
            </div>
            <div className="md:col-span-2 bg-[#ef2435] rounded-3xl p-7 relative overflow-hidden text-white glow-red">
              <div className="font-mono text-[11px] tracking-widest mb-2 opacity-90 font-semibold">{t("home_bento_4_label", "CUSTOM MIX")}</div>
              <h3 className="font-display text-2xl mb-3">{t("home_bento_4_title", "TAILORED FOR YOU")}</h3>
              <ArrowUpRight size={28}/>
            </div>
            <div className="md:col-span-2 card-dark rounded-3xl p-7 relative">
              <div className="font-mono text-[11px] tracking-widest text-[#ef2435] mb-2 font-semibold">[ 05 ]</div>
              <h3 className="font-display text-2xl text-gray-900 mb-2">{t("home_bento_5_title", "NHAI / IRC APPROVED")}</h3>
              <p className="text-gray-500 text-xs">{t("home_bento_5_text", "Compliance certified")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-24 bg-white border-y border-[#ececef] hidden">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 mb-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className="section-eyebrow">{t("home_products_eyebrow", "[ 02 / PRODUCT_LINEUP ]")}</span>
              <h2 className="giant-text text-5xl md:text-7xl font-bold leading-[0.9]">{t("home_products_title", "THE FULL SPECTRUM.")}</h2>
            </div>
            <Link to="/products" className="btn-ghost px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">All products <ArrowRight size={16}/></Link>
          </div>
        </div>
        <div className="overflow-x-auto pb-6">
          <div className="flex gap-5 px-5 md:px-8 w-max">
            {products.map((p, i) => {
              const grades = Array.isArray(p.grades) ? p.grades : String(p.grades || "").split(",").map(g => g.trim()).filter(Boolean);
              return (
                <div key={p.id || i} className="card-dark rounded-3xl w-[320px] md:w-[360px] p-7 shrink-0 group cursor-pointer bg-[#fafafa]">
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-mono text-xs text-[#ef2435] font-semibold">[ {String(i+1).padStart(2,'0')} ]</span>
                    <ArrowUpRight className="text-gray-400 group-hover:text-[#ef2435] transition-colors" size={20}/>
                  </div>
                  <h3 className="font-display text-4xl text-gray-900 mb-1">{p.name}</h3>
                  <p className="text-xs text-[#ef2435] uppercase tracking-widest mb-5 font-mono font-semibold">{p.type}</p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 line-clamp-3">{p.description || p.desc}</p>
                  <div className="flex flex-wrap gap-1.5 pt-5 border-t border-[#ececef]">
                    {grades.map(g => <span key={g} className="text-xs px-2.5 py-1 rounded-md bg-white border border-[#ececef] text-gray-700 font-mono">{g}</span>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="py-24 md:py-32 bg-[#fafafa] hidden">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-10 mb-16 items-end">
            <div>
              <span className="section-eyebrow">{t("home_projects_eyebrow", "[ 03 / SELECTED_WORK ]")}</span>
              <h2 className="giant-text text-5xl md:text-7xl font-bold leading-[0.9]">{t("home_projects_title", "PROJECTS WE'VE POURED.")}</h2>
            </div>
            <Link to="/works" className="btn-ghost bg-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2 justify-self-start lg:justify-self-end">Browse all <ArrowRight size={16}/></Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {projects.slice(0,6).map((w, i) => {
              const tall = i === 0 || i === 4;
              return (
                <article key={w.id || i} className={`group relative rounded-3xl overflow-hidden bg-white border border-[#ececef] hover:border-[#ef2435] transition-all hover:shadow-xl ${tall ? 'md:row-span-2' : ''}`}>
                  <div className={`overflow-hidden ${tall ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}>
                    <img src={w.image || w.img} alt={w.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80"/>
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <p className="font-mono text-[10px] text-[#ff5060] tracking-widest mb-2 font-semibold">[ {String(i+1).padStart(2,'0')} / {String(w.client || '').toUpperCase()} ]</p>
                    <h3 className="font-display text-2xl md:text-3xl text-white mb-1">{w.title}</h3>
                    <p className="text-gray-200 text-sm line-clamp-2 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity max-w-md">{w.description || w.desc}</p>
                  </div>
                  <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/95 backdrop-blur flex items-center justify-center group-hover:bg-[#ef2435] transition-colors">
                    <ArrowUpRight className="text-gray-900 group-hover:text-white" size={16}/>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Big stats */}
      <section className="py-20 bg-white border-y border-[#ececef] hidden">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[{n:200,s:"K+",l:"Cubic meters"},{n:150,s:"+",l:"Projects"},{n:36,s:"+",l:"Brand partners"},{n:99,s:"%",l:"On-time delivery"}].map((s,i)=>(
            <div key={i} className="text-center md:text-left">
              <p className="font-mono text-xs text-[#ef2435] mb-3 font-semibold">[ {String(i+1).padStart(2,'0')} ]</p>
              <p className="giant-text text-6xl md:text-7xl font-bold"><Counter end={s.n} suffix={s.s}/></p>
              <p className="text-gray-500 text-sm mt-2 uppercase tracking-wider">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Clients */}
      <section className="py-20 bg-[#fafafa] hidden">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 mb-10">
          <span className="section-eyebrow">[ 04 / TRUSTED_BY ]</span>
          <h2 className="giant-text text-4xl md:text-5xl font-bold leading-tight max-w-3xl">Industry leaders count on us.</h2>
        </div>
        <div className="overflow-hidden border-y border-[#ececef] py-8 bg-white">
          <div className="marquee">
            {[...clients, ...clients].map((c, i) => (
              <div key={i} className="shrink-0 w-44 h-16 flex items-center justify-center px-8">
                <img src={c.logo || c.src} alt={c.name} className="client-logo max-h-10 w-auto" onError={(e)=>{e.target.style.display='none';e.target.parentElement.innerHTML=`<span class='text-gray-500 font-mono text-sm font-semibold'>${c.name}</span>`}}/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-white hidden">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 mb-14 items-end">
            <div>
              <span className="section-eyebrow">{t("home_testimonials_eyebrow", "[ 05 / VOICES ]")}</span>
              <h2 className="giant-text text-5xl md:text-7xl font-bold leading-[0.9]">{t("home_testimonials_title", "WORDS THAT POUR HEAVY.")}</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.slice(0,6).map((r, i) => (
              <div key={r.id || i} className="card-dark rounded-3xl p-7 relative bg-[#fafafa]">
                <Quote className="text-[#ef2435]/20 absolute top-6 right-6" size={36}/>
                <div className="flex gap-0.5 mb-5">
                  {Array(Number(r.rating) || 5).fill().map((_,j)=><Star key={j} size={14} className="fill-[#ef2435] text-[#ef2435]"/>)}
                </div>
                <p className="text-gray-700 leading-relaxed text-[15px] mb-6">“{r.content || r.text}”</p>
                <div className="pt-5 border-t border-[#ececef] flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#ef2435]/10 flex items-center justify-center text-[#ef2435] font-bold text-sm">{(r.name || '').charAt(0)}</div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{r.name}</p>
                    {r.company && <p className="text-xs text-gray-500">{r.company}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#fafafa] relative overflow-hidden hidden">
        <div className="absolute inset-0 grid-bg opacity-50"/>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-15 blur-3xl" style={{background:'radial-gradient(circle, #ef2435, transparent 70%)'}}/>
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 relative text-center">
          <span className="section-eyebrow justify-center" style={{display:'inline-flex'}}>{t("home_cta_eyebrow", "[ READY_TO_BUILD ]")}</span>
          <h2 className="giant-text text-6xl md:text-9xl font-bold leading-[0.85] mb-8">{t("home_cta_title_1", "LET'S POUR")}<br/><span className="giant-red">{t("home_cta_title_2", "SOMETHING BIG.")}</span></h2>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10 text-lg">{t("home_cta_description", "From single-pour residential slabs to multi-thousand cubic meter infrastructure projects. Tell us your scope and we'll engineer the right mix.")}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-primary px-8 py-4 rounded-full text-sm font-semibold inline-flex items-center gap-2">Request a quote <ArrowUpRight size={18}/></Link>
            <a href={`tel:${settings.phone || '+91-9861934840'}`} className="btn-ghost bg-white px-8 py-4 rounded-full text-sm font-semibold inline-flex items-center gap-2">Call us now</a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;



